import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import Brand from "./Brand.jsx";
import { subscribeWallets } from "../services/wallets.js";
import {
    getWalletConnectProvider,
    isWalletConnectConfigured,
    resetWalletConnectProvider
} from "../services/walletconnect.js";
import { Compass } from "lucide-react";
import { useModalA11y } from "../hooks/useModalA11y.js";

/**
 * Wallet selection modal shown when the user clicks "Connect Wallet" in
 * the navbar. Has two views:
 *   - "select" (default): MetaMask
 *   - "rejected": shown when MetaMask returns a user-rejection error.
 *     Surfaces the reason plus three actions: Try Again, Back to Home, Close.
 *
 * Closes on backdrop click or Escape unless `busy` is set. Locks body
 * scroll while open. Renders via React portal so the dark navbar (z-50)
 * stays visible above this modal (z-40).
 */
export default function WalletConnectModal({
    open,
    busy = false,
    view = "select",
    rejectionMessage,
    onClose,
    onRetry,
    onSelectWallet
}) {
    // Body scroll lock + a11y plumbing. Escape + focus trap + return-focus
    // are owned by the shared `useModalA11y` hook so every modal in the
    // app behaves consistently; body overflow lock stays local because no
    // other modal needs it (the wallet flow can be tall enough to scroll
    // the page underneath, which would be disorienting).
    const dialogRef = useRef(null);
    useModalA11y({
        open,
        onClose: () => !busy && onClose?.(),
        containerRef: dialogRef
    });
    useEffect(() => {
        if (!open) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-40 overflow-y-auto bg-[#f6f6ec]"
            onClick={() => !busy && onClose?.()}
            role="presentation"
        >
            <div className="flex min-h-full items-center justify-center p-4 pt-20">
                <div
                    ref={dialogRef}
                    // Fixed modal frame so every view (selector, Wallet
                    // Connect entry/pending, not-detected, rejected) renders
                    // at the same width and height. Views opt into the
                    // remaining vertical space via `flex flex-col` + a
                    // wrapper that grows.
                    className="flex w-full max-w-md flex-col rounded-2xl bg-vb-panel ring-1 ring-[#213e28] shadow-card p-6 min-h-[500px]"
                    onClick={(event) => event.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="wallet-modal-title"
                >
                    <div className="flex justify-center border-b-2 border-[#213e28] pb-5">
                        <Brand tone="dark" size="lg" />
                    </div>

                    <div className="flex flex-1 flex-col">
                        {view === "rejected" ? (
                            <RejectedView message={rejectionMessage} onRetry={onRetry} onClose={onClose} />
                        ) : (
                            <SelectView busy={busy} onSelectWallet={onSelectWallet} />
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

function SelectView({ busy, onSelectWallet }) {
    const [wallets, setWallets] = useState([]);
    const [otherOpen, setOtherOpen] = useState(false);
    const [comingSoon, setComingSoon] = useState(null);
    // WalletConnect view state machine:
    //   null         → wallet selector visible
    //   "init"       → SDK loading; spinner shown
    //   "qr"         → QR code visible, waiting for user to scan + approve
    //   "connecting" → user scanned, mobile wallet is finalising session
    //   "error"      → init / connect failed; surfaced to the user
    const [wcStep, setWcStep] = useState(null);
    const [wcUri, setWcUri] = useState("");
    const [wcError, setWcError] = useState(null);
    const wcCancelledRef = useRef(false);
    useEffect(() => subscribeWallets(setWallets), []);

    // Match each fixed option to a wallet announced via EIP-6963. The rdns
    // values are the canonical reverse-domain ids the wallets register
    // themselves under, so this stays robust even if the user has multiple
    // injected wallets installed.
    const findByRdns = (rdns) => wallets.find((w) => w.info.rdns === rdns);
    const metaMask = findByRdns("io.metamask");
    const rabby = findByRdns("io.rabby");
    const coinbase = findByRdns("com.coinbase.wallet");
    const knownRdns = new Set(["io.metamask", "io.rabby", "com.coinbase.wallet"]);
    const otherWallets = wallets.filter((w) => !knownRdns.has(w.info.rdns));

    function handleMetaMask() {
        if (metaMask) return onSelectWallet(metaMask.provider, "metamask");
        if (typeof window !== "undefined" && window.ethereum?.isMetaMask) {
            return onSelectWallet(window.ethereum, "metamask");
        }
        setComingSoon({
            label: "MetaMask not detected",
            message: "Install MetaMask from metamask.io and refresh this page."
        });
    }

    function handleRabby() {
        if (rabby) return onSelectWallet(rabby.provider, "rabby");
        setComingSoon({
            label: "Rabby Wallet not detected",
            message: "Install Rabby Wallet from rabby.io and refresh this page."
        });
    }

    function handleCoinbase() {
        if (coinbase) return onSelectWallet(coinbase.provider, "coinbase");
        setComingSoon({
            label: "Coinbase Wallet not detected",
            message: "Install Coinbase Wallet from coinbase.com/wallet and refresh this page."
        });
    }

    // When the user picked Safe (rather than the generic WalletConnect
    // option), we still use the WalletConnect transport under the hood
    // (Safe is a smart-contract wallet without an injected provider)
    // but tag the connection as "safe" so the badge + copy reflect
    // their actual wallet of choice. `wcFlavorRef` is read by the QR
    // view + the kind tagged on `onSelectWallet`.
    const wcFlavorRef = useRef("walletconnect");

    function handleSafe() {
        wcFlavorRef.current = "safe";
        handleWalletConnect("safe");
    }

    async function handleWalletConnect(flavor = "walletconnect") {
        if (!isWalletConnectConfigured()) {
            setComingSoon({
                label: "WalletConnect not configured",
                message:
                    "Set VITE_WALLETCONNECT_PROJECT_ID in your .env to a project id from cloud.walletconnect.com, then reload."
            });
            return;
        }
        wcFlavorRef.current = flavor;
        wcCancelledRef.current = false;
        setWcUri("");
        setWcError(null);
        setWcStep("init");
        try {
            const provider = await getWalletConnectProvider();
            if (wcCancelledRef.current) return;

            // Already connected — short-circuit straight to the dapp.
            if (provider.session) {
                onSelectWallet(provider, wcFlavorRef.current);
                return;
            }

            // The SDK fires `display_uri` with the pairing URI as soon as
            // it has one. We feed it to the QR view via setWcUri.
            const onDisplayUri = (uri) => {
                if (wcCancelledRef.current) return;
                setWcUri(uri);
                setWcStep("qr");
            };
            provider.on("display_uri", onDisplayUri);

            try {
                await provider.connect();
                provider.removeListener?.("display_uri", onDisplayUri);
                if (wcCancelledRef.current) return;
                setWcStep("connecting");
                onSelectWallet(provider, wcFlavorRef.current);
            } catch (err) {
                provider.removeListener?.("display_uri", onDisplayUri);
                if (wcCancelledRef.current) return;
                setWcError(err?.message || "Connection cancelled.");
                setWcStep("error");
            }
        } catch (err) {
            if (wcCancelledRef.current) return;
            setWcError(err?.message || "Failed to initialise WalletConnect.");
            setWcStep("error");
        }
    }

    async function cancelWalletConnect() {
        wcCancelledRef.current = true;
        setWcStep(null);
        setWcUri("");
        setWcError(null);
        // Tear down so the next attempt starts with a fresh URI instead
        // of resuming the abandoned pairing.
        try {
            await resetWalletConnectProvider();
        } catch {
            /* ignore — best-effort cleanup */
        }
    }

    if (wcStep === "init" || wcStep === "qr" || wcStep === "connecting") {
        return (
            <WalletConnectQrView
                step={wcStep}
                uri={wcUri}
                flavor={wcFlavorRef.current}
                onCancel={cancelWalletConnect}
                onRetry={() => {
                    cancelWalletConnect().then(() => handleWalletConnect(wcFlavorRef.current));
                }}
            />
        );
    }

    if (wcStep === "error") {
        return (
            <WalletConnectErrorView
                message={wcError}
                onRetry={() => {
                    cancelWalletConnect().then(() => handleWalletConnect());
                }}
                onBack={cancelWalletConnect}
            />
        );
    }

    if (comingSoon) {
        return (
            <ComingSoonView
                label={comingSoon.label}
                message={comingSoon.message}
                onBack={() => setComingSoon(null)}
            />
        );
    }

    return (
        <>
            <h3 id="wallet-modal-title" className="mt-6 text-center text-lg font-bold text-vb-text">
                Connect Wallet
            </h3>
            <p className="mt-2 text-center text-xs text-vb-text-soft">
                Securely link your digital assets to start managing your private vault.
            </p>

            <ul className="mt-6 space-y-3">
                <WalletOption
                    icon={<MetaMaskIcon />}
                    label="MetaMask"
                    busy={busy}
                    onClick={handleMetaMask}
                />
                <WalletOption
                    icon={<RabbyIcon />}
                    label="Rabby"
                    busy={busy}
                    onClick={handleRabby}
                />
                <WalletOption
                    icon={<CoinbaseIcon />}
                    label="Coinbase Wallet"
                    busy={busy}
                    onClick={handleCoinbase}
                />
                <WalletOption
                    icon={<SafeIcon />}
                    label="Safe"
                    busy={busy}
                    onClick={handleSafe}
                />
                <WalletOption
                    icon={<WalletConnectIcon />}
                    label="WalletConnect"
                    busy={busy}
                    onClick={() => handleWalletConnect()}
                />

                {otherOpen && otherWallets.length > 0 ? (
                    <li className="rounded-xl bg-[#F0FFF5] px-3 py-3 ring-1 ring-[#213e28]">
                        <ul className="space-y-2">
                            {otherWallets.map(({ info, provider }) => (
                                <WalletOption
                                    key={info.uuid}
                                    icon={info.icon ? <img src={info.icon} alt="" className="h-5 w-5" /> : <GenericWalletIcon />}
                                    label={info.name}
                                    busy={busy}
                                    onClick={() => onSelectWallet(provider, "other")}
                                />
                            ))}
                        </ul>
                    </li>
                ) : null}
            </ul>

            <div className="mt-6 border-t-2 border-[#213e28] pt-4 text-center">
                <div className="inline-flex items-center justify-center gap-1.5 font-manrope text-[13px] font-bold text-[#1D3E29]">
                        <Compass className="h-4 w-4" />

                    Getting started with Web3?
                </div>
                <p className="mt-1 text-[11px] text-[#1D3E29]/65">
                    Explore our guide to learn the basics and get started
                </p>
                {/* <a
                    href="#"
                    className="mt-2 inline-flex items-center gap-1 text-[12px] font-bold text-[#1D7A3F] hover:underline"
                >
                    Learn More
                    <ArrowRightSmall />
                </a> */}
            </div>
        </>
    );
}


function ArrowRightSmall() {
    return (
        <svg
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <line x1="3" y1="10" x2="16" y2="10" />
            <polyline points="11 5 16 10 11 15" />
        </svg>
    );
}

/**
 * SDK-driven WalletConnect flow. Three states:
 *   - "init"       → SDK loading; spinner shown.
 *   - "qr"         → real session URI received; user scans the QR with a
 *                    mobile wallet to pair.
 *   - "connecting" → user scanned + approved; final session handshake in
 *                    progress (this is normally over in <1s, the parent
 *                    closes the modal as soon as `onSelectWallet` fires).
 *
 * The pairing URI comes from the `display_uri` event the SDK emits on
 * `provider.connect()` and is rendered as a real QR code via the
 * `qrcode` library.
 */
function WalletConnectQrView({ step, uri, flavor = "walletconnect", onCancel, onRetry }) {
    const isSafe = flavor === "safe";
    const [qrDataUrl, setQrDataUrl] = useState("");

    // Regenerate the QR image whenever the URI changes. Errors are
    // surfaced as a Retry button rather than swallowed silently.
    useEffect(() => {
        if (!uri) {
            setQrDataUrl("");
            return;
        }
        let cancelled = false;
        QRCode.toDataURL(uri, {
            margin: 1,
            width: 280,
            color: { dark: "#1D3E29", light: "#FFFFFF" }
        })
            .then((dataUrl) => {
                if (!cancelled) setQrDataUrl(dataUrl);
            })
            .catch(() => {
                if (!cancelled) setQrDataUrl("");
            });
        return () => {
            cancelled = true;
        };
    }, [uri]);

    async function copyUri() {
        if (!uri) return;
        try {
            await navigator.clipboard.writeText(uri);
        } catch {
            /* ignore — clipboard may be unavailable in insecure contexts */
        }
    }

    const initialising = step === "init" || (step === "qr" && !qrDataUrl);
    const finalising = step === "connecting";

    return (
        <>
            <div
                className="mt-6 flex items-center justify-center gap-2"
                style={{ color: isSafe ? "#12FF80" : "#1E84FF" }}
            >
                {isSafe ? (
                    /* The on-disk safe_wallet.svg was recoloured to gold
                       so all wallet rows + header badges look uniform.
                       The Safe-flavoured QR view, however, should keep
                       the real Safe brand identity — green vault on a
                       green circle. We render that inline so the file
                       swap doesn't follow us here. */
                    <SafeBrandLogo size={32} />
                ) : (
                    <WalletConnectFullIcon size={32} />
                )}
                <h3 className="font-manrope text-[20px] font-bold">
                    {isSafe ? "Safe" : "Wallet Connect"}
                </h3>
            </div>
            <p className="mt-2 text-center text-[12px] text-vb-text-soft">
                {finalising
                    ? isSafe
                        ? "Confirm the connection inside your Safe app."
                        : "Finalising session — confirm in your wallet."
                    : isSafe
                    ? "Open Safe → Apps → WalletConnect, then scan this QR. Or paste the URI inside the Safe app."
                    : "Open your mobile wallet and scan the QR code to connect."}
            </p>

            <div className="mt-4 flex justify-center">
                <div className="relative inline-block rounded-[10px] bg-white p-2 ring-1 ring-[#213e28]">
                    {qrDataUrl ? (
                        <img
                            src={qrDataUrl}
                            alt={isSafe ? "Safe pairing QR code" : "WalletConnect pairing QR code"}
                            className="block h-44 w-44"
                        />
                    ) : (
                        <div className="flex h-44 w-44 items-center justify-center">
                            <Spinner />
                        </div>
                    )}
                    {qrDataUrl ? (
                        isSafe ? (
                            /* Safe logo is already a self-contained
                               green circle — no white wrapper / square
                               needed. We position the SVG itself so
                               only the circle silhouette sits over the
                               QR, with no surrounding white chip. */
                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                <SafeBrandLogo size={32} />
                            </span>
                        ) : (
                            <span className="absolute left-1/2 top-1/2 inline-flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[8px] bg-[#1E84FF] text-white shadow ring-2 ring-white">
                                <WalletConnectFullIcon size={22} className="text-white" />
                            </span>
                        )
                    ) : null}
                </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                <span
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-1 text-[10px] font-semibold ${
                        finalising
                            ? "bg-[#189747] text-white"
                            : initialising
                            ? "bg-[#1D3E29]/10 text-[#1D3E29]"
                            : "bg-[#1D3E29] text-[#FBC206]"
                    }`}
                >
                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#FBC206]" />
                    {finalising
                        ? "Approval Received"
                        : initialising
                        ? "Initialising…"
                        : "Waiting for scan"}
                </span>
                <button
                    type="button"
                    onClick={copyUri}
                    disabled={!uri}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/55 px-3 py-1 text-[10px] font-semibold text-[#1D3E29] ring-1 ring-[#213e28] hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <CopyIcon />
                    Copy URI
                </button>
                <button
                    type="button"
                    onClick={onRetry}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/55 px-3 py-1 text-[10px] font-semibold text-[#1D3E29] ring-1 ring-[#213e28] hover:bg-white"
                >
                    <RefreshIcon />
                    New QR
                </button>
            </div>

            <div className="mt-auto border-t-2 border-[#213e28] pt-4">
                <div className="flex justify-center">
                    <button
                        className="rounded-full border-2 border-[#1D3E29] px-5 py-2 text-sm font-bold text-[#1D3E29] hover:bg-[#1D3E29] hover:text-white transition-colors"
                        onClick={onCancel}
                    >
                        Back to wallets
                    </button>
                </div>
            </div>
        </>
    );
}

function WalletConnectErrorView({ message, onRetry, onBack }) {
    return (
        <div className="flex flex-1 flex-col">
            <div className="flex flex-1 flex-col items-center justify-center text-center">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#FDE2E2] ring-1 ring-[#C93030]/40">
                    <svg
                        className="h-7 w-7 text-[#C93030]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="9" />
                        <line x1="12" y1="8" x2="12" y2="13" />
                        <line x1="12" y1="16.5" x2="12" y2="17" />
                    </svg>
                </span>
                <h3 className="mt-4 text-lg font-bold text-vb-text">WalletConnect Failed</h3>
                <p className="mt-2 max-w-xs text-sm text-vb-text-soft">
                    {message || "The session was rejected or timed out. Try again."}
                </p>
            </div>
            <div className="border-t-2 border-[#213e28] pt-4">
                <div className="grid grid-cols-2 gap-3">
                    <button
                        className="rounded-full bg-[#FBC206] px-4 py-2 text-sm font-bold text-[#1D3E29] hover:-translate-y-0.5 hover:shadow-md transition-all"
                        onClick={onRetry}
                    >
                        Try Again
                    </button>
                    <button
                        className="rounded-full border-2 border-[#1D3E29] px-4 py-2 text-sm font-bold text-[#1D3E29] hover:bg-[#1D3E29] hover:text-white transition-colors"
                        onClick={onBack}
                    >
                        Back to Wallets
                    </button>
                </div>
            </div>
        </div>
    );
}

function Spinner() {
    return (
        <span
            aria-hidden="true"
            className="h-10 w-10 animate-spin rounded-full border-[3px] border-[#213e28] border-t-[#1D3E29]"
        />
    );
}

function CopyIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4" y="4" width="9" height="10" rx="1.5" />
            <path d="M11 2H3.5A1.5 1.5 0 0 0 2 3.5V11" />
        </svg>
    );
}

function RefreshIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 10a7 7 0 0 1 12-4.95L17 7" />
            <polyline points="17 3 17 7 13 7" />
            <path d="M17 10a7 7 0 0 1-12 4.95L3 13" />
            <polyline points="3 17 3 13 7 13" />
        </svg>
    );
}

function WalletConnectFullIcon({ size = 18, className = "text-[#1E84FF]" }) {
    return (
        <WalletConnectMark
            className={className}
            style={{ width: size, height: size }}
        />
    );
}

/**
 * Inline WalletConnect double-rainbow mark with `currentColor` fill so
 * each callsite picks the brand colour it needs (dark green in the
 * wallet selection list, blue in the entry / pending headers).
 */
function WalletConnectMark({ className = "", style }) {
    return (
        <svg
            viewBox="0 0 49 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className={className}
            style={style}
        >
            <path
                fill="currentColor"
                d="M10.0307 5.86035C18.0238 -1.95345 30.9782 -1.95345 38.9693 5.86035L39.9309 6.80061C40.0259 6.89246 40.1014 7.00245 40.1529 7.12403C40.2045 7.24562 40.2311 7.37631 40.2311 7.50836C40.2311 7.64041 40.2045 7.77111 40.1529 7.89269C40.1014 8.01427 40.0259 8.12426 39.9309 8.21611L36.6418 11.4326C36.5944 11.4801 36.538 11.5178 36.476 11.5435C36.414 11.5692 36.3475 11.5824 36.2804 11.5824C36.2133 11.5824 36.1468 11.5692 36.0848 11.5435C36.0228 11.5178 35.9664 11.4801 35.919 11.4326L34.594 10.1395C29.0202 4.68756 19.9818 4.68756 14.4081 10.1395L12.9911 11.5264C12.894 11.6205 12.764 11.6731 12.6288 11.6731C12.4935 11.6731 12.3635 11.6205 12.2664 11.5264L8.97724 8.30789C8.88183 8.21651 8.8059 8.1068 8.75403 7.98534C8.70216 7.86389 8.67542 7.73321 8.67542 7.60116C8.67542 7.46912 8.70216 7.33844 8.75403 7.21698C8.8059 7.09553 8.88183 6.98581 8.97724 6.89443L10.0307 5.86035ZM45.7721 12.5156L48.6998 15.3793C48.7948 15.4711 48.8703 15.5811 48.9219 15.7027C48.9734 15.8243 49 15.955 49 16.087C49 16.2191 48.9734 16.3498 48.9219 16.4713C48.8703 16.5929 48.7948 16.7029 48.6998 16.7948L35.4984 29.7076C35.3043 29.8952 35.0448 30 34.7747 30C34.5046 30 34.2451 29.8952 34.0509 29.7076L24.6838 20.5436C24.6355 20.4955 24.5702 20.4685 24.502 20.4685C24.4339 20.4685 24.3686 20.4955 24.3203 20.5436L14.9511 29.7076C14.757 29.8952 14.4975 30 14.2274 30C13.9573 30 13.6978 29.8952 13.5036 29.7076L0.300173 16.7948C0.205213 16.7029 0.129705 16.5929 0.0781391 16.4713C0.0265728 16.3498 0 16.2191 0 16.087C0 15.955 0.0265728 15.8243 0.0781391 15.7027C0.129705 15.5811 0.205213 15.4711 0.300173 15.3793L3.22792 12.5156C3.42209 12.3281 3.6816 12.2232 3.95169 12.2232C4.22177 12.2232 4.48129 12.3281 4.67546 12.5156L14.0446 21.6797C14.1467 21.7776 14.308 21.7776 14.4081 21.6797L23.7773 12.5156C23.9714 12.3281 24.2309 12.2232 24.501 12.2232C24.7711 12.2232 25.0306 12.3281 25.2248 12.5156L34.594 21.6797C34.6961 21.7776 34.8553 21.7776 34.9574 21.6797L44.3266 12.5156C44.5199 12.3273 44.7793 12.2218 45.0493 12.2218C45.3194 12.2218 45.5788 12.3273 45.7721 12.5156Z"
            />
        </svg>
    );
}

function ComingSoonView({ label, message, onBack }) {
    return (
        <div className="flex flex-1 flex-col">
            <div className="flex flex-1 flex-col items-center justify-center text-center">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-vb-accent/30 ring-1 ring-vb-accent">
                    <svg
                        className="h-7 w-7 text-vb-text"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 2" />
                    </svg>
                </span>
                <h3 className="mt-4 text-lg font-bold text-vb-text">{label}</h3>
                <p className="mt-2 max-w-xs text-sm text-vb-text-soft">{message}</p>
            </div>
            <div className="border-t-2 border-[#213e28] pt-4">
                <div className="flex justify-center">
                    <button className="btn-primary" onClick={onBack}>
                        Back to wallets
                    </button>
                </div>
            </div>
        </div>
    );
}

function GenericWalletIcon() {
    return (
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2.5" y="5" width="15" height="11" rx="2" />
            <path d="M2.5 8h15" />
            <circle cx="14" cy="12" r="0.8" fill="currentColor" />
        </svg>
    );
}

function WalletConnectIcon() {
    return <WalletConnectMark className="h-5 w-5 text-[#b98c23]" />;
}

// Full-colour brand SVGs as <img>. The user-uploaded assets are
// PNG-traced filled SVGs — neither alpha-mask nor CSS filter can
// recolour them without collapsing the logo into a blob. Rendering
// them at their natural brand colours preserves recognition; the cost
// is a colour mismatch against MetaMask + WalletConnect which DO mask
// cleanly. If we ever ship proper outline-only versions of these
// three, we can switch to the gold-mask treatment for full uniformity.
function CoinbaseIcon() {
    return (
        <img
            src="/images/coinBase.svg"
            alt=""
            className="h-5 w-5 rounded object-contain"
        />
    );
}

function OtherIcon() {
    return (
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-[#b98c23]" aria-hidden="true">
            <circle cx="4" cy="10" r="1.6" />
            <circle cx="10" cy="10" r="1.6" />
            <circle cx="16" cy="10" r="1.6" />
        </svg>
    );
}

function MaskIcon({ src, className = "" }) {
    return (
        <span
            aria-hidden="true"
            className={`inline-block shrink-0 ${className}`}
            style={{
                backgroundColor: "currentColor",
                WebkitMaskImage: `url(${src})`,
                maskImage: `url(${src})`,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                WebkitMaskSize: "contain",
                maskSize: "contain"
            }}
        />
    );
}

function RejectedView({ message, onRetry, onClose }) {
    return (
        <div className="flex flex-1 flex-col">
            <div className="flex flex-1 flex-col items-center justify-center text-center">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-100 ring-1 ring-red-300">
                    <svg
                        className="h-7 w-7 text-red-700"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="9" />
                        <line x1="12" y1="8" x2="12" y2="13" />
                        <line x1="12" y1="16.5" x2="12" y2="17" />
                    </svg>
                </span>
                <h3 id="wallet-modal-title" className="mt-4 text-lg font-bold text-vb-text">
                    Connection Rejected
                </h3>
                <p className="mt-2 max-w-xs text-sm text-vb-text-soft">
                    {message || "You declined the wallet connection request. You can try again or return to the home page."}
                </p>
            </div>

            <div className="border-t-2 border-[#213e28] pt-4">
                <div className="grid grid-cols-2 gap-3">
                    <button className="btn-primary justify-center" onClick={onRetry}>
                        Try Again
                    </button>
                    <Link to="/" className="btn-secondary justify-center" onClick={onClose}>
                        Back to Home
                    </Link>
                </div>
                <button
                    className="mt-3 w-full rounded-full px-5 py-2.5 text-sm font-semibold text-vb-text-soft hover:text-vb-text"
                    onClick={onClose}
                >
                    Close
                </button>
            </div>
        </div>
    );
}

function WalletOption({ icon, label, onClick, disabled, busy, comingSoon, center }) {
    return (
        <li>
            <button
                disabled={disabled || busy}
                onClick={onClick}
                title={comingSoon ? "Coming soon" : undefined}
                /* Hover signals: keep bg the same cream (so the icons'
                   cream cutout fills stay invisibly blended) and switch
                   to a gold ring + darker text instead. Previously
                   `hover:bg-[#d4d4c9]` darkened the row, which made
                   every <img> wallet icon's cream cutout area pop as a
                   bright square against the darker chip. */
                className={`flex w-full items-center justify-between rounded-xl bg-[#f6f6ec] px-4 py-3 text-sm font-semibold text-[#9a8955] ring-1 ring-[#1D3E29] transition-all enabled:hover:ring-2 enabled:hover:ring-[#FBC206] enabled:hover:text-[#1D3E29] disabled:cursor-not-allowed disabled:opacity-60 ${
                    center ? "justify-center" : ""
                }`}
            >
                <span className="flex items-center gap-3 whitespace-nowrap">
                    {icon ? (
                        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-vb-text-soft">
                            {icon}
                        </span>
                    ) : null}
                    <span className="truncate">{label}</span>
                </span>
                {!center ? <ChevronRight /> : null}
            </button>
        </li>
    );
}

function ChevronRight() {
    return (
        <svg
            className="h-4 w-4  text[#b98c23]"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}

function MetaMaskIcon() {
    return <MaskIcon src="/images/metaMaskIcon.svg" className="h-5 w-5 text-[#b98c23]" />;
}

function RabbyIcon() {
    return (
        <img
            src="/images/rabby.svg"
            alt=""
            className="h-5 w-5 rounded object-contain"
        />
    );
}

function SafeIcon() {
    return (
        <img
            src="/images/safe_wallet.svg"
            alt=""
            className="h-5 w-5 rounded object-contain"
        />
    );
}

/**
 * Safe brand identity glyph — green vault on green circle. Inline so it
 * keeps the real brand colours even though the on-disk Safe SVG has
 * been recoloured to gold for the rest of the app. Used only on the
 * Safe-flavoured QR view where the user has explicitly chosen Safe.
 */
function SafeBrandLogo({ size = 32 }) {
    return (
        <svg
            viewBox="0 0 32 32"
            width={size}
            height={size}
            aria-hidden="true"
        >
            <circle cx="16" cy="16" r="14" fill="#12FF80" />
            <path
                d="M10 13c0 2.53-.39 3-2.5 3C5.37 16 5 15.54 5 12.91c0-2.21.55-3.24 1.91-3.6C7.96 9.04 9.04 7.96 9.31 6.91 9.74 5.29 10.75 5 15.91 5 21.74 5 22 5.11 22 7.5S21.73 10 16 10h-6zM22.69 25.09c-.43 1.62-1.44 1.91-6.6 1.91-5.83 0-6.09-.11-6.09-2.5s.27-2.5 6-2.5h6v-3c0-2.53.39-3 2.5-3 2.13 0 2.5.46 2.5 3.09 0 2.21-.55 3.24-1.91 3.6-1.05.27-2.13 1.35-2.4 2.4z"
                fill="#0E2C19"
            />
        </svg>
    );
}